import React, { type ReactNode, useContext, useMemo } from 'react'

import { ToastContext } from './ToastContext'
import type { Options } from './toastTypes'

/**
 * This hook allows you to render [toast notifications](https://en.wikipedia.org/wiki/Pop-up_notification).
 *
 * ## ToastProvider
 *
 * In order to use the `useToast` hook, you’ll need to wrap your application in a `ToastProvider`.
 *
 * ## Accessibility
 *
 * You can use the `role` option to specify an ARIA role for the rendered toast. Two roles are currently supported
 *
 * - the default [`status` ARIA role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_status_role)
 * - the [`alert` ARIA role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Alert_Role)
 */
export function useToast(): {
  addToast: (content: ReactNode, opts?: Options) => void
} {
  const context = useContext(ToastContext)

  if (context === null) {
    throw new Error('useToast must be called inside <ToastProvider>')
  }

  return useMemo(
    () => ({
      addToast: (content, opts) => {
        context.add(content, opts)
      },
    }),
    [context]
  )
}

// Note(@auster.chen): This is only exported for storybook docs
/**
 * This hook allows you to render [toast notifications](https://en.wikipedia.org/wiki/Pop-up_notification).
 *
 * ## ToastProvider
 *
 * In order to use the `useToast` hook, you’ll need to wrap your application in a `ToastProvider`.
 *
 * ## Accessibility
 *
 * You can use the `role` option to specify an ARIA role for the rendered toast. Two roles are currently supported
 *
 * - the default [`status` ARIA role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_status_role)
 * - the [`alert` ARIA role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Alert_Role)
 *
 *
 * ## Usage
 *
 * `useToast` will return a `addToast` function, please look in the docs table for the parameters passed to the `addToast` function. Usage: `addToast(content, addToastOptions)`.
 *
 */
export function addToastParameters(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _: {
    /**
     * React node to pass to show as the content of the toast
     */
    content: ReactNode
    /**
     * Set of options for toast, see `addToastOptions` for detailed options
     */
    addToastOptions?: Options
  }
) {
  return <div />
}

// Note(@auster.chen): This is only exported for storybook docs
export function addToastOptions(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _: Options
) {
  return <div />
}
